import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_METHODS = ["VISA", "Mastercard", "AMEX", "PayPal", " Pay", "G Pay"];

const formatPrice = (price, currency) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(price);

export const CheckoutDrawer = ({ book, open, onClose }) => {
  const [qty, setQty] = useState(1);
  const [lang, setLang] = useState("de");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLang("de");
    setQty(1);
  }, [book]);

  const EN_VARIANT = {
    lookup_key: "schattenpsyche_band1_en",
    title: "Schattenpsyche · Volume 1",
    subtitle: "The Profiler's Handbook for Everyday Life",
    language: "English",
  };
  const effective =
    book && book.lookup_key === "schattenpsyche_band1" && lang === "en"
      ? { ...book, ...EN_VARIANT }
      : book;

  const startCheckout = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: effective.lookup_key,
        quantity: qty,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Die Kaufabwicklung konnte nicht gestartet werden. Bitte versuche es erneut.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && book && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-sm"
            data-testid="checkout-modal-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 260, damping: 30 }}
            className="fixed top-0 right-0 bottom-0 z-[100] w-full sm:max-w-md bg-void-soft border-l border-white/10 flex flex-col"
            data-testid="checkout-modal-dialog"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400">
                  Bestellübersicht
                </p>
                <h3 className="font-cinzel text-xl font-bold text-white mt-1">Sichere Kaufabwicklung</h3>
              </div>
              <button
                onClick={onClose}
                data-testid="checkout-modal-close-button"
                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-white/50 transition-colors"
                aria-label="Kaufabwicklung schließen"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
              {book.lookup_key === "schattenpsyche_band1" && (
                <div
                  className="flex rounded-full border border-white/10 p-1 bg-white/5"
                  data-testid="checkout-language-toggle"
                >
                  {[
                    ["de", "Deutsch"],
                    ["en", "English"],
                  ].map(([code, label]) => (
                    <button
                      key={code}
                      onClick={() => setLang(code)}
                      data-testid={`checkout-lang-${code}`}
                      className={`flex-1 py-2.5 rounded-full font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-200 ${
                        lang === code ? "bg-white text-void" : "text-slate-400 hover:text-white"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex gap-4 items-center glass rounded-xl p-4">
                <img
                  src={effective.cover}
                  alt={`${effective.title} Cover`}
                  className="w-16 drop-shadow-[0_10px_16px_rgba(0,0,0,0.7)]"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-cinzel font-bold text-white truncate">{effective.title}</p>
                  <p className="font-typewriter text-xs text-paper truncate">{effective.subtitle}</p>
                  <p className="font-mono text-xs text-slate-500 mt-1">
                    {formatPrice(effective.price, effective.currency)} / Exemplar · {effective.language}
                  </p>
                </div>
                <div className="flex items-center gap-2 border border-white/10 rounded-full px-2 py-1">
                  <button
                    onClick={() => setQty(Math.max(1, qty - 1))}
                    data-testid="checkout-qty-decrease"
                    className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
                    aria-label="Menge verringern"
                  >
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <span className="font-mono text-sm text-white w-5 text-center" data-testid="checkout-quantity">
                    {qty}
                  </span>
                  <button
                    onClick={() => setQty(Math.min(10, qty + 1))}
                    data-testid="checkout-qty-increase"
                    className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
                    aria-label="Menge erhöhen"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <div className="space-y-3 border-t border-white/10 pt-5">
                <div className="flex justify-between text-sm text-slate-400">
                  <span>Zwischensumme</span>
                  <span className="font-mono text-white" data-testid="checkout-subtotal">
                    {formatPrice(effective.price * qty, effective.currency)}
                  </span>
                </div>
                <div className="flex justify-between text-sm text-slate-400">
                  <span>Lieferung</span>
                  <span className="font-mono text-xs">Sofort-Download per E-Mail</span>
                </div>
                <div className="flex justify-between text-sm text-slate-400">
                  <span>Steuern</span>
                  <span className="font-mono text-xs">wird an der Kasse berechnet</span>
                </div>
              </div>

              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">
                  Bezahle, wie du willst
                </p>
                <div className="flex flex-wrap gap-2" data-testid="checkout-payment-methods">
                  {PAYMENT_METHODS.map((m) => (
                    <span
                      key={m}
                      className="px-3 py-1.5 rounded-md border border-white/10 bg-white/5 font-mono text-[11px] tracking-wider text-slate-300"
                    >
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="px-6 py-6 border-t border-white/10 space-y-4">
              <button
                onClick={startCheckout}
                disabled={loading}
                data-testid="checkout-submit-pay-button"
                className="glow-btn w-full disabled:opacity-60 disabled:pointer-events-none"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sichere Kasse wird geöffnet…
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    Weiter zur Zahlung · {formatPrice(effective.price * qty, effective.currency)}
                  </>
                )}
              </button>
              <p className="flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                <Lock className="w-3 h-3" />
                256-Bit SSL · Abgesichert durch Stripe
              </p>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
